/**
 * InspectorModal - Click-to-view detailed inspection overlay
 * Features isolated 3D preview with rotating model
 */

import { Suspense, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Environment } from '@react-three/drei';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import * as THREE from 'three';
import { useStore, InspectedObjectData } from '../../store/useStore';
import { MercuryRelayStation, GEOReceiverStation, EAHPSatelliteModel } from '../r3f/SpacecraftModels';

// 3D Preview component that renders the isolated model
function ModelPreview({ objectId }: { objectId: string }) {
  const groupRef = useRef<THREE.Group>(null);

  // Slow rotation for showcase
  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.3;
    }
  });

  return (
    <group ref={groupRef}>
      {objectId === 'mercuryRelay' && (
        <MercuryRelayStation position={[0, 0, 0]} />
      )}
      {objectId === 'geoReceiver' && (
        <GEOReceiverStation position={[0, 0, 0]} />
      )}
      {objectId === 'eahpSwarm' && (
        <group scale={5}>
          <EAHPSatelliteModel />
        </group>
      )}
    </group>
  );
}

// Preview scene with lighting and effects
function PreviewScene({ objectId }: { objectId: string }) {
  return (
    <>
      <PerspectiveCamera makeDefault position={[2, 1.5, 2]} fov={50} />
      <OrbitControls
        enableZoom={true}
        enablePan={false}
        autoRotate={false}
        minDistance={1}
        maxDistance={5}
      />

      {/* Lighting */}
      <ambientLight intensity={0.3} />
      <directionalLight position={[5, 5, 5]} intensity={1} color={0xffffff} />
      <directionalLight position={[-3, 2, -2]} intensity={0.5} color={0x4488ff} />
      <pointLight position={[0, 2, 0]} intensity={0.5} color={0xff8800} />

      {/* Background */}
      <color attach="background" args={['#0a0a15']} />

      {/* Model */}
      <ModelPreview objectId={objectId} />

      {/* Effects */}
      <EffectComposer>
        <Bloom
          intensity={1.2}
          luminanceThreshold={0.2}
          luminanceSmoothing={0.9}
        />
      </EffectComposer>
    </>
  );
}

// Specification row component
function SpecRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1 border-b border-gray-700/50">
      <span className="text-gray-400 text-xs font-mono">{label}</span>
      <span className="text-cyan-400 text-xs font-mono">{value}</span>
    </div>
  );
}

// Main Inspector Modal component
export function InspectorModal() {
  const { inspectedObject, closeInspector } = useStore();

  if (!inspectedObject) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={closeInspector}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal Content */}
      <div
        className="relative w-full max-w-5xl h-[80vh] bg-gray-900/95 border border-cyan-500/30 rounded-lg overflow-hidden flex"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left side - 3D Preview */}
        <div className="w-1/2 h-full border-r border-gray-700">
          <div className="absolute top-4 left-4 z-10">
            <span className="px-3 py-1 bg-cyan-500/20 border border-cyan-500/50 rounded text-cyan-400 text-xs font-mono">
              3D PREVIEW
            </span>
          </div>

          <Canvas
            gl={{
              antialias: true,
              toneMapping: THREE.ACESFilmicToneMapping,
              toneMappingExposure: 1,
            }}
            dpr={[1, 2]}
          >
            <Suspense fallback={null}>
              <PreviewScene objectId={inspectedObject.id} />
            </Suspense>
          </Canvas>

          <div className="absolute bottom-4 left-4 text-gray-500 text-xs font-mono">
            Drag to rotate | Scroll to zoom
          </div>
        </div>

        {/* Right side - Technical Specifications */}
        <div className="w-1/2 h-full overflow-y-auto p-6">
          {/* Close button */}
          <button
            onClick={closeInspector}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-800 hover:bg-red-600 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header */}
          <div className="mb-6">
            <div className="text-xs text-cyan-500 font-mono mb-1">INSPECTING</div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {inspectedObject.name}
            </h2>
            <div className="flex gap-2">
              <span className="px-2 py-1 bg-orange-500/20 border border-orange-500/50 rounded text-orange-400 text-xs font-mono">
                {inspectedObject.type}
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <h3 className="text-sm font-mono text-gray-400 mb-2">DESCRIPTION</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              {inspectedObject.description}
            </p>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <div className="text-xs text-gray-500 font-mono mb-1">MATERIAL</div>
              <div className="text-sm text-white">{inspectedObject.material}</div>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <div className="text-xs text-gray-500 font-mono mb-1">THERMAL LOAD</div>
              <div className="text-sm text-orange-400">{inspectedObject.thermalLoad}</div>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 col-span-2">
              <div className="text-xs text-gray-500 font-mono mb-1">POWER OUTPUT</div>
              <div className="text-lg text-green-400 font-mono">{inspectedObject.powerOutput}</div>
            </div>
          </div>

          {/* Technical Specifications */}
          <div className="mb-6">
            <h3 className="text-sm font-mono text-gray-400 mb-3">TECHNICAL SPECIFICATIONS</h3>
            <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
              {Object.entries(inspectedObject.specifications).map(([key, value]) => (
                <SpecRow key={key} label={key} value={value} />
              ))}
            </div>
          </div>

          {/* Status indicator */}
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-green-400 font-mono">OPERATIONAL</span>
          </div>
        </div>
      </div>
    </div>
  );
}
